import { ReactNode } from "react";

import { HolderOutlined } from "@ant-design/icons";
import { DropIndicator } from "@atlaskit/pragmatic-drag-and-drop-react-drop-indicator/box";
import { cx } from "class-variance-authority";
import { motion } from "motion/react";

import { rawTransition, tokens } from "../../styles/theme.css";

import { useItemDraggable } from "./hooks/useItemDraggable";
import { useItemDropTarget } from "./hooks/useItemDropTarget";
import { useListMonitor } from "./hooks/useListMonitor";
import * as theme from "./ReorderableList.css";

// theme var is in ms; framer expects seconds.
const LAYOUT_TRANSITION = { duration: rawTransition.time / 1000 };

export interface ReorderableListProps<T> {
    items: ReadonlyArray<T>;
    getId: (item: T) => string;
    renderItem: (item: T) => ReactNode;
    onReorder: (newItems: Array<T>) => void;
    /**
     * Returns `true` for items that cannot be dragged. Disabled items still
     * render but their handle is inert.
     */
    isItemDisabled?: (item: T) => boolean;
    /**
     * When `true`, disabled items are also locked - other items cannot be
     * dragged right before them.
     */
    lockDisabledItems?: boolean;
    className?: string;
}

export function ReorderableList<T>({
    items,
    getId,
    renderItem,
    onReorder,
    isItemDisabled,
    lockDisabledItems = false,
    className,
}: ReorderableListProps<T>) {
    const { listId, isDndReordering } = useListMonitor({ items, onReorder });

    const isLockedItems = items.map((item) =>
        lockDisabledItems && isItemDisabled ? isItemDisabled(item) : false
    );

    return (
        <ul className={cx(theme.list, className)}>
            {items.map((item, index) => {
                const disabled = isItemDisabled ? isItemDisabled(item) : false;
                return (
                    <ReorderableItem
                        key={getId(item)}
                        listId={listId}
                        index={index}
                        isDisabled={disabled}
                        isLockedItems={isLockedItems}
                        animateLayout={!isDndReordering}
                    >
                        {renderItem(item)}
                    </ReorderableItem>
                );
            })}
        </ul>
    );
}

interface ReorderableItemProps {
    listId: string;
    index: number;
    isDisabled: boolean;
    isLockedItems: ReadonlyArray<boolean>;
    animateLayout: boolean;
    children: ReactNode;
}

function ReorderableItem({
    listId,
    index,
    isDisabled,
    isLockedItems,
    animateLayout,
    children,
}: ReorderableItemProps) {
    const { itemRef, handleRef, isDragging } = useItemDraggable({
        disabled: isDisabled,
        listId,
        index,
        isLockedItems,
    });
    const dropEdge = useItemDropTarget({ itemRef, listId, index });

    return (
        <motion.li
            ref={itemRef}
            layout={animateLayout ? "position" : false}
            transition={LAYOUT_TRANSITION}
            className={cx(theme.item, isDragging && theme.itemDragging)}
        >
            <div
                ref={handleRef}
                className={cx(theme.handle, isDisabled && theme.handleDisabled)}
                aria-hidden={isDisabled}
            >
                <HolderOutlined aria-hidden />
            </div>
            <div className={theme.content}>{children}</div>
            {dropEdge && (
                <DropIndicator edge={dropEdge} indent={tokens.spacing[4]} />
            )}
        </motion.li>
    );
}
